/**
 * HubSyllabusBadgeList — 학습 화면 AI 트래커 아래 칸의 「강의」 보기(설계 FE-20260914 §3·§6·§7).
 *
 * 강의 v2 나무를 지금 모양 그대로(SyllabusTree 조회 모드 — 시험지 조립 창의 강의 칸과 같은 부품) 그리고,
 * 조건 있는 행 끝(renderTrailing)에 난이도 1~5 정답률 배지 다섯을 단다. 값은 서버 집계
 * GET /api/v2/syllabi/{id}/progress 한 번에서 받는다(계약 BE-20260914-reply-student-syllabus-progress.md).
 *
 * 강의 고르기: 이름 단추 → 강의 바꾸기 판(SyllabusSwitcher, [새 강의] 끔). 고른 강의는 이 브라우저에 **학생마다** 기억한다(D3).
 * 기억이 없는 학생은 이 브라우저에서 마지막으로 고른 강의로 시작하고 그 사실을 한 줄로 알린다 — 강사가 고를 때만 그 학생의 강의로 저장한다.
 * 기억한 강의가 지워졌거나 볼 수 없게 되면 기억을 지우고 「강의를 고르세요」로 돌아간다.
 * 펼침은 강의 관리 화면·조립 창과 같은 세션 열쇠(openState)라, 관리 화면에서 펼친 줄이 여기서도 펼쳐져 있다.
 *
 * 집계 요청은 강의를 고를 때·학생이 바뀔 때(부모가 학생마다 다시 올린다)·[다시 시도] 때만 나간다. 펼치기·보기 전환으로는 부르지 않는다.
 * 늦은 응답이 지금 화면을 덮지 않게 요청마다 취소 신호를 걸고, (학생, 강의)별 응답은 화면이 떠 있는 동안 이 모듈에 기억한다 —
 * [매스컨셉]↔[강의]를 오가며 이 부품이 내려갔다 올라와도 다시 부르지 않는다. 응답의 판 번호가 나무와 다르면 나무를 새로 받고 한 번만 다시 부른다.
 *
 * 행을 누르면 부모(onPickRow)가 오른쪽 문제 미리보기를 「강의 행 보기」로 바꾼다 — 그 행의 검색 조건 그대로 × 이 학생의
 * v2 풀이 기록(설계 §8 D10, useSyllabusRowProblems). 차트에는 그 행의 개념을 강조한다. 고른 면은 부모가 그 행을 쓰는 동안만 선다.
 */
import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { AlertTriangle, ChevronDown, ChevronsDownUp, ChevronsUpDown } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../common';
import SyllabusTree from '../SyllabusV2/SyllabusTree';
import SyllabusSwitcher from '../SyllabusV2/SyllabusSwitcher';
import { progressCache } from './hubSyllabusCache';
import { buildIndex } from '../SyllabusV2/treeIndex';
import { readOpenIds, writeOpenIds } from '../SyllabusV2/openState';
import { getSyllabus, getSyllabusProgress, describeSyllabusError } from '../SyllabusV2/syllabusApi';
import { fetchConceptDictV2 } from '../ExamPapersV2/conceptBadge';
import HubDifficultyBadges from './HubDifficultyBadges';
import {
  badgeCells, conceptIdsOf, indexProgress, lostCount, progressErrorState,
} from './syllabusProgress';

const ringCls = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary';
const studentKey = (studentId) => `students-hub:syllabus:${studentId}`;
const LAST_KEY = 'students-hub:syllabus:last';
/** 브라우저 기억 — 막혀 있어도(사생활 보호 창 등) 화면은 선다. 기억은 곁들이는 상태다 */
const readKey = (k) => { try { return window.localStorage.getItem(k); } catch (e) { return null; } };
const writeKey = (k, v) => {
  try {
    if (v == null) window.localStorage.removeItem(k); else window.localStorage.setItem(k, String(v));
  } catch (e) { /* 곁들이는 상태 */ }
};

const IDLE = { status: 'idle', res: null, err: null };

function HubSyllabusBadgeList({
  studentId, headerLead = null, activeRowKey = null, onPickRow,
}) {
  const { getAccessToken } = useAuth();
  const [sid, setSid] = useState(() => readKey(studentKey(studentId)) ?? readKey(LAST_KEY));
  const [fromLast, setFromLast] = useState(() => readKey(studentKey(studentId)) == null && readKey(LAST_KEY) != null);
  const [notice, setNotice] = useState(null);
  const [switchOpen, setSwitchOpen] = useState(false);
  const switchRef = useRef(null);
  const [detailKey, setDetailKey] = useState(0);
  const [detail, setDetail] = useState({ loading: false, data: null, error: null });
  const [expanded, setExpanded] = useState(new Set());
  const [prog, setProg] = useState(IDLE);
  const [progNonce, setProgNonce] = useState(0);
  const revRetried = useRef(new Set());
  const [dict, setDict] = useState(null);

  // 행을 누를 때 칩에 적을 개념 이름 — v2 개념 사전(모듈 캐시). 못 받아도 칩은 번호로 선다
  useEffect(() => {
    let alive = true;
    Promise.resolve(fetchConceptDictV2(getAccessToken)).then((d) => { if (alive) setDict(Array.isArray(d) ? d : null); });
    return () => { alive = false; };
  }, [getAccessToken]);

  /** 기억한 강의를 더 쓸 수 없음(지워짐·볼 수 없음) — 이 학생·최근 기억에서 지우고 고르기로 돌아간다 */
  const forget = useCallback((gone) => {
    if (readKey(studentKey(studentId)) === String(gone)) writeKey(studentKey(studentId), null);
    if (readKey(LAST_KEY) === String(gone)) writeKey(LAST_KEY, null);
    setSid(null);
    setFromLast(false);
    setNotice('기억해 둔 강의를 더 볼 수 없어 지웠습니다. 강의를 고르세요.');
  }, [studentId]);

  // 고른 강의의 나무 — 펼침은 관리 화면·조립 창과 같은 세션 열쇠
  useEffect(() => {
    if (sid == null) { setDetail({ loading: false, data: null, error: null }); return undefined; }
    let alive = true;
    setDetail({ loading: true, data: null, error: null });
    getSyllabus(sid, getAccessToken)
      .then((d) => {
        if (!alive) return;
        setDetail({ loading: false, data: d, error: null });
        setExpanded(readOpenIds(sid));
      })
      .catch((e) => {
        if (!alive) return;
        if (e?.envelope && (e.httpStatus === 404 || e.reason === 'out_of_scope')) { forget(sid); return; }
        setDetail({ loading: false, data: null, error: describeSyllabusError(e) });
      });
    return () => { alive = false; };
  }, [sid, getAccessToken, detailKey, forget]);

  const data = detail.data && String(detail.data.syllabus_id) === String(sid) ? detail.data : null;
  const revision = Number.isInteger(data?.revision) ? data.revision : null;

  // 집계 — 강의·학생마다 한 번. 캐시가 같은 판이면 부르지 않는다
  useEffect(() => {
    if (!data || !Number.isInteger(studentId)) { setProg(IDLE); return undefined; }
    const key = `${studentId}:${data.syllabus_id}`;
    const cached = progressCache.get(key);
    if (cached && (revision == null || cached.syllabus_revision === revision)) {
      setProg({ status: 'ok', res: cached, err: null });
      return undefined;
    }
    const ctrl = new AbortController();
    setProg({ status: 'loading', res: null, err: null });
    getSyllabusProgress(data.syllabus_id, { studentId, signal: ctrl.signal }, getAccessToken)
      .then((res) => {
        if (ctrl.signal.aborted) return;
        // 세는 동안 강의가 고쳐졌다 — 나무를 새로 받으면 이 효과가 다시 돈다(같은 강의에서 한 번만)
        if (revision != null && Number.isInteger(res.syllabus_revision) && res.syllabus_revision !== revision && !revRetried.current.has(key)) {
          revRetried.current.add(key);
          setDetailKey((k) => k + 1);
          return;
        }
        progressCache.set(key, res);
        setProg({ status: 'ok', res, err: null });
      })
      .catch((e) => {
        if (ctrl.signal.aborted || e?.name === 'AbortError') return;
        const st = progressErrorState(e);
        if (st.kind === 'syllabus_gone') { forget(data.syllabus_id); return; }
        setProg({ status: 'error', res: null, err: st });
      });
    return () => ctrl.abort();
  }, [data, revision, studentId, getAccessToken, progNonce, forget]);

  const retryProgress = () => {
    if (data) progressCache.delete(`${studentId}:${data.syllabus_id}`);
    setProgNonce((n) => n + 1);
  };

  const index = useMemo(() => buildIndex(data?.items || []), [data]);
  // 하위가 있는 행 — 전체 펼치기가 펼 대상. 최상위 묶음(부모 없음)의 열쇠는 행이 아니라 뺀다(조립 창과 같은 계산)
  const expandable = useMemo(() => [...index.children.keys()].filter((k) => index.byId.has(k)), [index]);
  const allOpen = expandable.length > 0 && expandable.every((k) => expanded.has(k));
  const progIdx = useMemo(() => (prog.status === 'ok' ? indexProgress(prog.res) : null), [prog]);
  const nameOf = useMemo(() => {
    const m = new Map();
    (dict || []).forEach((c) => m.set(String(c.id ?? c.concept_id), c.name ?? c.concept_name ?? ''));
    return m;
  }, [dict]);

  /** 행의 개념 — 사전이 있으면 지금 없는(지워진) 개념은 뺀다. 사전을 못 받았으면 식 그대로 */
  const rowConcepts = useCallback((it) => {
    const ids = conceptIdsOf(it?.query);
    return nameOf.size ? ids.filter((c) => nameOf.has(String(c))) : ids;
  }, [nameOf]);

  const toggle = (nid, open) => {
    setExpanded((cur) => {
      const next = new Set(cur);
      if (open) next.add(String(nid)); else next.delete(String(nid));
      if (sid != null) writeOpenIds(sid, next);
      return next;
    });
  };
  const toggleAll = () => {
    const next = allOpen ? new Set() : new Set(expandable);
    if (sid != null) writeOpenIds(sid, next);
    setExpanded(next);
  };

  const chooseSyllabus = (next) => {
    setSwitchOpen(false);
    const s = String(next);
    writeKey(studentKey(studentId), s);
    writeKey(LAST_KEY, s);
    setSid(s);
    setFromLast(false);
    setNotice(null);
  };

  // 판은 지금 강의를 다시 고르면 onSwitch 를 부르지 않는다(바뀐 게 없으니) — 최근 강의로 시작한 학생은
  // 그 고름이 곧 「이 학생의 강의」라 여기서 기억한다. 다른 강의를 골랐으면 뒤이어 온 onSwitch 가 덮는다
  const closeSwitcher = (why) => {
    if (why === 'picked' && fromLast && sid != null) chooseSyllabus(sid);
    else setSwitchOpen(false);
  };

  // 조건 행 누름 — 오른쪽 목록을 그 행의 검색 조건 그대로 거르게 하고(부모 → 문제 미리보기의 강의 행 보기, D10),
  // 차트에는 그 행의 개념을 강조한다. 개념 조건이 없는 행(라벨만 등)도 목록은 거른다. 같은 행을 다시 누르면 해제
  const choose = (it) => {
    setNotice(null);
    const key = `${sid}:${it.node_id}`;
    if (activeRowKey === key) { onPickRow?.(null); return; }
    const concepts = rowConcepts(it).map((cid) => ({ cid, label: nameOf.get(String(cid)) || '' }));
    onPickRow?.({ key, name: it.name, query: it.query, concepts });
  };

  // 고른 면 — 부모가 이 행을 강의 행 보기로 쓰는 동안만(다른 손으로 선택이 바뀌면 부모가 푼다)
  const selectedId = useMemo(() => {
    if (!activeRowKey || sid == null || !activeRowKey.startsWith(`${sid}:`)) return null;
    const nid = activeRowKey.slice(String(sid).length + 1);
    return index.byId.has(nid) ? nid : null;
  }, [activeRowKey, sid, index]);

  const renderTrailing = (it) => {
    if (it.query == null) return null;
    if (prog.status === 'loading' || (prog.status === 'idle' && data)) return <HubDifficultyBadges loading />;
    if (!progIdx) return null;
    const key = String(it.node_id);
    const skip = progIdx.skipped.get(key);
    if (skip) {
      return <span className="pr-1 text-[10px] font-medium text-red-500" title={skip.message || skip.reason}>세지 못함</span>;
    }
    const node = progIdx.byNode.get(key);
    if (!node) return null;
    const lost = lostCount(node);
    return (
      <span className="inline-flex items-center gap-1.5">
        {lost > 0 && (
          <span
            className="shrink-0 rounded border border-amber-300 px-1 text-[9px] font-medium text-amber-700"
            title={`조건에 지금 없는 항목 ${lost}개 — 그 값은 아무 문제도 잡지 않아 낮게 나올 수 있습니다`}
          >
            사라진 항목
          </span>
        )}
        <HubDifficultyBadges cells={badgeCells(node, it.query)} />
      </span>
    );
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-1">
      {/* 머리 띠 — 매스컨셉 보기의 띠와 같은 상자. 전환 단추(부모) · 강의 이름(=강의 바꾸기) · 셈 기준 · 전체 펼치기 */}
      <div className="flex shrink-0 items-center gap-2 rounded border border-gray-200 bg-gray-50 px-2 py-1">
        {headerLead}
        <button
          ref={switchRef}
          type="button"
          onClick={() => setSwitchOpen((v) => !v)}
          aria-haspopup="dialog"
          aria-expanded={switchOpen}
          aria-label={`강의 바꾸기 — 지금 ${data?.name || '고른 강의 없음'}`}
          title={data?.name ? `${data.name} — 눌러서 강의 바꾸기` : '강의 고르기'}
          // 강의 이름은 줄지 않는다(최대 45%) — 좁을 때 먼저 말줄임되는 것은 옆의 셈 기준 글자다
          className={`inline-flex min-w-0 max-w-[45%] shrink-0 items-center gap-0.5 rounded-lg px-1 py-0.5 text-left text-xs font-bold text-gray-800 transition-colors hover:bg-gray-100 ${ringCls}`}
        >
          <span className="min-w-0 truncate">{data?.name || (sid == null ? '강의 고르기' : '강의')}</span>
          <ChevronDown aria-hidden="true" className="size-3.5 shrink-0 text-gray-400" />
        </button>
        {data && (
          // 좁으면 이 글자가 먼저 말줄임된다 — 띠가 두 줄로 접혀 표 자리를 먹지 않게(매스컨셉 띠와 같은 높이)
          <span className="min-w-0 truncate text-[11px] text-gray-400" title="배지는 풀 때마다 센 정답률이고, 차트의 기간 선택과 무관하게 전체 기간입니다">
            풀이 기준 · 전체 기간{progIdx?.recordsProblems != null ? ` · 푼 문제 ${progIdx.recordsProblems.toLocaleString()}개` : ''}
          </span>
        )}
        <span className="flex-1" />
        {data && expandable.length > 0 && (
          <button
            type="button"
            onClick={toggleAll}
            className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs text-gray-600 transition-colors hover:bg-gray-100 ${ringCls}`}
          >
            {allOpen
              ? <ChevronsDownUp aria-hidden="true" className="size-3.5" />
              : <ChevronsUpDown aria-hidden="true" className="size-3.5" />}
            {allOpen ? '전체 접기' : '전체 펼치기'}
          </button>
        )}
      </div>

      {switchOpen && (
        <SyllabusSwitcher
          anchorRef={switchRef}
          currentId={sid}
          getAccessToken={getAccessToken}
          onSwitch={chooseSyllabus}
          onClose={closeSwitcher}
          canCreate={false}
        />
      )}

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded border border-gray-200 bg-white">
        {fromLast && sid != null && (
          <p className="shrink-0 border-b border-amber-100 bg-amber-50 px-3 py-1 text-[11px] text-amber-700">
            이 학생은 고른 강의가 없어 최근 강의로 시작했습니다. 강의 이름을 눌러 고르면 이 학생의 강의로 기억합니다.
          </p>
        )}
        {notice && <p className="shrink-0 border-b border-gray-100 bg-gray-50 px-3 py-1 text-[11px] text-gray-600">{notice}</p>}
        {prog.status === 'error' && (
          <div role="alert" className="flex shrink-0 items-center gap-2 border-b border-red-100 bg-red-50 px-3 py-1 text-[11px] text-red-600">
            <span className="min-w-0 flex-1">{prog.err.message}</span>
            {prog.err.retry && (
              <button type="button" onClick={retryProgress} className="shrink-0 underline hover:text-red-700">다시 시도</button>
            )}
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          {sid == null && (
            <div className="flex flex-col items-center gap-2 px-2 py-6 text-center text-xs text-gray-400">
              <p>이 학생에게 보여 줄 강의를 고르세요.</p>
              <Button size="xs" variant="secondary" onClick={() => setSwitchOpen(true)}>강의 고르기</Button>
            </div>
          )}
          {detail.loading && <p className="px-2 py-6 text-center text-xs text-gray-400">나무를 불러오는 중…</p>}
          {detail.error && (
            <div role="alert" className="flex items-start gap-2 rounded-2xl bg-red-50 p-3 text-xs text-red-600">
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
              <span className="min-w-0 flex-1">{detail.error}</span>
              <Button size="xs" variant="secondary" onClick={() => setDetailKey((n) => n + 1)}>다시 시도</Button>
            </div>
          )}
          {data && (
            <SyllabusTree
              items={data.items || []}
              mode="pick"
              readOnly
              selectedId={selectedId}
              onSelect={choose}
              expanded={expanded}
              onToggle={toggle}
              emptyText="이 강의에는 줄이 없습니다."
              renderTrailing={renderTrailing}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default HubSyllabusBadgeList;
